import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Drawer, Fab, IconButton, Stack, Typography } from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import { UserPermissionGate } from "@components/user-permission-gate";
import { Loader } from "@components/loader";
import { useSmAiChat } from "@hooks/use-sm-ai-chat";
import { useSmAiConversations } from "@hooks/use-sm-ai-conversations";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { getLocalStorageItem, setLocalStorageItem } from "@utils/index";
import type { SmAiConversation } from "@hooks/use-sm-ai-chat/types";
import { MessageList } from "./message-list";
import { ConversationList } from "./conversation-list";
import { Composer } from "./composer";
import { colors, styleConstants } from "@utils/colors";

type View = "chat" | "list";

export const SmAiChatWidget = () => {
  const { baseUrl } = useMultiSiteContext();
  const storageKey = `sm-ai-conversation-${baseUrl}`;

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>("list");
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);

  const {
    conversations,
    isLoading: isLoadingConversations,
    refresh: refreshConversations,
  } = useSmAiConversations();

  const activeConversation = conversations.find((c) => c.id === activeConversationId);

  // Resume the last active thread for this site, once, when the site resolves.
  const hasRestoredRef = useRef(false);
  useEffect(() => {
    if (!baseUrl || hasRestoredRef.current) return;
    hasRestoredRef.current = true;
    const storedId = getLocalStorageItem(storageKey) as string | null;
    if (storedId) setActiveConversationId(storedId);
  }, [baseUrl, storageKey]);

  // Keep the thread list fresh whenever the drawer is opened.
  useEffect(() => {
    if (open) refreshConversations();
  }, [open, refreshConversations]);

  const handleConversationCreated = useCallback(
    (conversation: SmAiConversation) => {
      setActiveConversationId(conversation.id);
      setLocalStorageItem(storageKey, conversation.id);
      refreshConversations();
    },
    [storageKey, refreshConversations]
  );

  const { messages, isStreaming, isLoadingHistory, available, sendMessage, stop } = useSmAiChat(
    activeConversationId,
    handleConversationCreated
  );

  const handleSelectConversation = (id: string) => {
    setActiveConversationId(id);
    setLocalStorageItem(storageKey, id);
    setView("chat");
  };

  const handleNewChat = () => {
    setActiveConversationId(null);
    setView("chat");
  };

  return (
    <UserPermissionGate caps="sm_ai_chat">
      <Fab
        onClick={() => {
          setOpen(true);
          setView("list");
        }}
        aria-label="Open SM AI chat"
        sx={{
          position: "fixed",
          // Bottom-left, opposite this app's own bottom-right "Add" affordances
          // (data-table desktop footer button + mobile TableMobileMenu quadrant),
          // so the two primary floating actions never compete for the same corner.
          left: 24,
          // On mobile, clear the fixed Scan/Filter/Search/Add bar (Material's
          // documented FAB-above-bottom-bar spacing: bar height + 16px margin).
          bottom: {
            xs: `calc(${styleConstants.mobileTableMenuHeight} + env(safe-area-inset-bottom) + 16px)`,
            md: 24,
          },
          zIndex: (theme) => theme.zIndex.appBar + 1,
          bgcolor: colors.blue,
          color: colors.surface,
          "&:hover": { bgcolor: colors.blueHover },
        }}
      >
        <ChatBubbleOutlineIcon />
      </Fab>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Stack sx={{ width: { xs: "100vw", sm: 420 }, height: "100%" }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 1, py: 1, bgcolor: colors.navy, color: colors.surface }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, pl: 0.5 }}>
                {view === "list" ? "Conversations" : activeConversation?.title || "SM AI"}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center">
              {view === "chat" && (
                <IconButton
                  onClick={() => setView("list")}
                  sx={{ color: colors.surface }}
                  aria-label="All chats"
                >
                  <ForumOutlinedIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton onClick={handleNewChat} sx={{ color: colors.surface }} aria-label="New chat">
                <AddCommentOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={() => setOpen(false)} sx={{ color: colors.surface }} aria-label="Close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>

          <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
            {view === "list" ? (
              <ConversationList
                conversations={conversations}
                isLoading={isLoadingConversations}
                activeConversationId={activeConversationId}
                onSelect={handleSelectConversation}
              />
            ) : isLoadingHistory ? (
              <Loader message="Loading conversation…" />
            ) : available ? (
              <MessageList
                messages={messages}
                isStreaming={isStreaming}
                open={open}
                onInternalLinkClick={() => setOpen(false)}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                SM AI isn't enabled for this site yet.
              </Typography>
            )}
          </Box>

          {view === "chat" && available && (
            <Composer onSend={sendMessage} onStop={stop} isStreaming={isStreaming} />
          )}
        </Stack>
      </Drawer>
    </UserPermissionGate>
  );
};
